import type {PaymentHistoryItem, PaymentMethodInfo, PlanInfo, SubscriptionInfo,} from "@/api/billing";
import type {AuthUser} from "@/state/authStore";
import {
    formatMinor,
    hasPaidAccess,
    resolveChargeAmountMinor,
    resolvePaywallMode,
    resolveRenewalPlanPrice,
} from "@/lib/subscription";

export {hasPaidAccess};

export function userDisplayName(user: AuthUser | null | undefined): string {
    if (!user) {
        return "Пользователь";
    }
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
    if (fullName.trim()) {
        return fullName;
    }
    if (user.telegram_username) {
        return `@${user.telegram_username}`;
    }
    return "Пользователь";
}

export function userTelegramLine(user: AuthUser | null | undefined): string {
    if (!user) {
        return "Telegram";
    }
    const handle = user.telegram_username
        ? `@${user.telegram_username}`
        : "Telegram подключен";
    return `${handle} · ID ${user.telegram_id}`;
}

export function userInitials(user: AuthUser | null | undefined): string {
    const name = userDisplayName(user);
    const parts = [user?.first_name, user?.last_name].filter(Boolean);
    if (parts.length) {
        return parts
            .map((part) => part!.slice(0, 1))
            .join("")
            .slice(0, 2)
            .toUpperCase();
    }
    return name.replace(/^@/, "").slice(0, 2).toUpperCase();
}

export function resolveSubscriptionPrice(
    subscription: SubscriptionInfo | null | undefined,
    user: AuthUser | null | undefined,
    plans: PlanInfo[] | undefined,
): string | null {
    const price = resolveRenewalPlanPrice(subscription, plans);
    if (!price) {
        return null;
    }
    const mode = resolvePaywallMode(
        user?.role === "LEADER",
        Boolean(user?.has_referral_binding),
    );
    return formatMinor(resolveChargeAmountMinor(price, mode), price.currency);
}

export function isAutorenewWithoutPaymentMethod(
    subscription: SubscriptionInfo | null | undefined,
    methods: PaymentMethodInfo[] | undefined,
): boolean {
    if (!subscription || subscription.cancel_at_period_end !== false) {
        return false;
    }
    return (methods?.length ?? 0) === 0;
}

export function findDefaultPaymentMethod(
    methods: PaymentMethodInfo[] | undefined,
): PaymentMethodInfo | null {
    if (!methods?.length) {
        return null;
    }
    return methods.find((m) => m.is_default) ?? methods[0] ?? null;
}

export function formatPaymentMethodLabel(
    method: PaymentMethodInfo | null | undefined,
): string | null {
    if (!method) {
        return null;
    }
    if (method.display_label) {
        return method.display_label;
    }
    const brand = method.brand ?? "Способ оплаты";
    if (method.last4) {
        return `${brand} **** ${method.last4}`;
    }
    return brand;
}

export function formatPaymentMethodLine(
    method: PaymentMethodInfo | null | undefined,
): string | null {
    const label = formatPaymentMethodLabel(method);
    if (!label) {
        return null;
    }
    if (method?.expires_month && method?.expires_year) {
        const month = String(method.expires_month).padStart(2, "0");
        const year = String(method.expires_year).slice(-2);
        return `${label} · ${month}/${year}`;
    }
    return label;
}

export function formatDate(
    value: string | null | undefined,
    options: Intl.DateTimeFormatOptions = {},
): string | null {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

export function daysUntil(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    const diff = date.getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / 86_400_000));
}

export function periodProgressPercent(
    start: string | null | undefined,
    end: string | null | undefined,
): number {
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;
    if (
        !startDate ||
        !endDate ||
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
    ) {
        return 0;
    }
    const total = endDate.getTime() - startDate.getTime();
    const used = Date.now() - startDate.getTime();
    if (total <= 0) {
        return 0;
    }
    return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
}

export function paymentHistoryTitle(item: PaymentHistoryItem): string {
    const amount = formatMinor(item.amount_minor, item.currency);
    if (item.payment_type === "PAYMENT_METHOD_BIND") {
        return `Привязка способа оплаты · ${amount}`;
    }
    if (item.payment_type === "RENEWAL") {
        return `Продление подписки · ${amount}`;
    }
    return `Оплата подписки · ${amount}`;
}

export function paymentHistoryBadge(item: PaymentHistoryItem): string {
    if (item.status === "SUCCEEDED") {
        return "Успешно";
    }
    if (item.status === "PENDING") {
        return "В обработке";
    }
    if (item.status === "FAILED" || item.status === "CANCELED") {
        return "Не прошла";
    }
    if (item.status === "REFUNDED") {
        return "Возврат";
    }
    return item.status;
}

export function shortOperationId(id: string): string {
    return id.replace(/-/g, "").slice(0, 8).toUpperCase();
}
